import type { TrialLifecycleState } from './email-notifications';

export type Tier = 'basic' | 'pro' | 'elite' | 'enterprise' | 'agency';

/**
 * Reports access rule (sidebar visibility + API gate must agree):
 *   - agency      → always (the feature is built for agencies)
 *   - enterprise  → only when the reportsEnabled flag is set (per-deal opt-in)
 *   - all others  → never
 * Single source of truth so the frontend Sidebar and the backend guard can't
 * drift. Takes the bits it needs rather than the whole Tenant to stay usable
 * from the partial shapes the frontend holds.
 */
export function canAccessReports(
  tier: Tier | undefined,
  reportsEnabled: boolean | undefined,
): boolean {
  if (tier === 'agency') return true;
  if (tier === 'enterprise') return reportsEnabled === true;
  return false;
}

export interface QuotaOverrides {
  scansPerMonth?: number;
  pagesPerAudit?: number;
  pagesPerDomain?: number;
  apiCallsPerDay?: number;
  concurrentAgents?: number;
  users?: number;
  sites?: number;
  brands?: number;
  zones?: number;
  languages?: number;
  competitors?: number;
  /** Total tasks per tenant (registry `tasks` gauge). Used for the agency
   *  "adjustable hard quota" and any negotiated enterprise cap. */
  tasks?: number;
  /** Shared prompts gauge (registry `prompts` metric, tenant-wide across the 3
   *  BP axes). Supersedes the per-axis brand*Queries fields below for the gate. */
  prompts?: number;
  socialIntegrations?: number;
  brandNarrativeQueries?: number;
  brandAuthorityQueries?: number;
  brandReferralQueriesPerLevel?: number;
  brandNarrativeRunsPerMonth?: number;
  brandAuthorityRunsPerMonth?: number;
  brandReferralRunsPerMonth?: number;
  /** MCP + public REST API access (`TierLimits.webhookAccess`). ON on every tier
   *  since pricing v2 D4 (2026-11-04; was elite+); override `false` to cut one tenant off. */
  webhookAccess?: boolean;
  /**
   * Product module capacity (registry `catalog_items` gauge): how many indexed
   * items — products + category pages, summed across ALL catalogues of ALL the
   * tenant's brands — the tenant may persist. Every non-agency tier defaults to
   * 0, so this (or a `catalog_items` pack) is what actually unlocks the module's
   * capacity for enterprise. Pairs with `product_reviews`.
   */
  catalog_items?: number;
  /**
   * Product module monthly optimization allowance (registry `product_reviews`
   * meter): items pushed through the optimize pipeline per calendar month. Resets
   * monthly like content_creations; a re-optimization costs a fresh unit. Set
   * alongside `catalog_items` when unlocking the module for an enterprise deal.
   */
  product_reviews?: number;
  /**
   * Cibles: establishment row cap — tenant-wide (every establishment costs a Geoapify geocoding credit
   * from the platform-wide 3 000/day budget). Default 100 on every tier; raise per tenant on request.
   * Registry `establishments` gauge. D34.
   */
  establishments?: number;
  /**
   * Cibles: signal cap per brand — the ONLY per-brand quota override.
   * The search Lambda loads one brand's index per container; a tenant-wide pool would starve
   * an agency's brands. Default 500k. Registry `target_signals` gauge. D13.
   * ⚠ Nothing may SUM this across brands — it is a per-brand cap, not a pool.
   */
  target_signals?: number;
  /**
   * Conversations: tenant-wide cap on non-archived Personas. Pairs with the
   * `conversationsEnabled` feature flag (D60 — both required for the sidebar item
   * to appear and every API endpoint to answer). Default 0 on all tiers; set
   * a non-zero value to grant beta access. -1 = unlimited (agency system ceiling).
   */
  conversation_personas?: number;
  /**
   * Per-brand personas (reusable BrandPersona cards, Brand Knowledge « Leviers de mesure »).
   * PER-BRAND gauge — never sum across brands.  Default 100 on every tier.  Registry `brand_personas`.
   */
  brand_personas?: number;
  /**
   * Prompt Studio: per-brand monthly prompt requests allowance (C18 / C17.1, 2026-10-15).
   * 1 prompt × 1 engine × 1 launch = 1 request.  PER-BRAND — never sum across brands.
   * Read only through `resolveBrandAllowances`.  Registry `brand_prompt_requests`.
   */
  brand_prompt_requests?: number;
  /**
   * Prompt Studio: per-brand monthly conversation runs allowance (C18 / C17.1, 2026-10-15).
   * PER-BRAND — never sum across brands.  Read only through `resolveBrandAllowances`.
   * Registry `brand_conversation_runs`.
   */
  brand_conversation_runs?: number;
  /**
   * Prompt Studio: structural cap on active prompt rows per brand (00b DQ14, 2026-10-15).
   * NOT a quota — never shown as one.  Ceiling 7 500 (system ceiling in the registry).
   * Registry `brand_prompts_max`.
   */
  brand_prompts_max?: number;
}

/**
 * Feature flags toggled per tenant on the admin page.
 * Each flag is enforced on both the frontend (hides the page/button) and the backend (rejects the call).
 * All default to false for new tenants — you explicitly enable per deal.
 */
export interface FeatureFlags {
  newsWatchEnabled?: boolean;
  competitorsEnabled?: boolean;
  tasksEnabled?: boolean;
  /**
   * Enables the Reports section (manual, white-label audit + status reports for
   * agency use cases). Toggled manually by platform admin — no tier gate.
   */
  reportsEnabled?: boolean;
  /**
   * Enables the Product module (retailer-only: Catalog / Product Optimization /
   * GEO Measurement / Content Generation). OFF by default on EVERY tier, agency
   * included (was force-ON for agency 2026-08-10 → 2026-11-03); toggled manually
   * by platform admin per tenant. Self-serve/enterprise tiers must also be given
   * catalog_items + product_reviews capacity (both 0 by default).
   * See docs/features/retailer-catalog.md.
   */
  productModuleEnabled?: boolean;
  /**
   * Surfaces the normally admin-only Agency XS plan (240€/4000cr) on this
   * tenant's own billing page so it can self-checkout to it. Agency-tier only.
   * Toggled from the admin tenant detail page.
   */
  agencyXsOffer?: boolean;
  /**
   * Surfaces the normally admin-only Agency Démarrage plan (100€/1660cr) on
   * this tenant's own billing page so it can self-checkout to it. Agency-tier
   * only. Toggled from the admin tenant detail page.
   */
  agencyDemarrageOffer?: boolean;
  /**
   * Conversations feature. Forced ON for every tier by `effectiveFeatureFlags`
   * since 2026-11-03 (was a per-tenant admin beta toggle, D60) — the stored
   * value is ignored. The QUOTA is the gate: the sidebar item and the Prompt
   * Studio tab only appear when `conversation_personas` (and, for the tab,
   * `brand_conversation_runs`) resolves non-zero.
   */
  conversationsEnabled?: boolean;
}

/**
 * Effective feature flags for a tenant. AGENCY gets every feature ON by default
 * (the offer is all-inclusive) regardless of what's stored; every other tier
 * uses its stored flags. Applied at the single tenant-load chokepoint
 * (TenantContextGuard) so the backend guard, the Sidebar, and every page read
 * the same resolved flags — no per-call-site special-casing. Single source of
 * truth, mirrors canAccessReports.
 *
 * ALWAYS resolve through this helper — never read `settings.featureFlags.<flag>`
 * off a raw DDB tenant row. A raw read silently disagrees with the HTTP path for
 * agency tenants whose stored flag was never toggled. Non-HTTP callers that must
 * resolve it themselves (no TenantContextGuard in the path): the measure-schedule
 * cron in packages/api/src/lambda.ts and BrandQueriesService.getTierContext.
 * (MeteringController reads the flag directly but is safe — it takes the
 * guard-resolved @CurrentTenant(), not a fresh DDB read.)
 */
export function effectiveFeatureFlags(
  tier: Tier | undefined,
  flags: FeatureFlags | undefined,
): FeatureFlags {
  if (tier === 'agency') {
    return {
      newsWatchEnabled: true,
      competitorsEnabled: true,
      tasksEnabled: true,
      reportsEnabled: true,
      // Per-tenant admin opt-in, like every other tier (was force-ON for agency
      // 2026-08-10 → 2026-11-03). Must be explicitly carried through since this
      // branch doesn't spread `flags` — omitting it would drop a stored `true`.
      productModuleEnabled: flags?.productModuleEnabled ?? false,
      // Per-tenant offer surfacing (Agency XS / Démarrage) — must be
      // explicitly carried through since this branch doesn't spread `flags`.
      agencyXsOffer: flags?.agencyXsOffer ?? false,
      agencyDemarrageOffer: flags?.agencyDemarrageOffer ?? false,
      conversationsEnabled: true,
    };
  }
  // Tasks + News Watch + Conversations are baseline features for every tier
  // (not admin-gated): the differentiator is the QUOTA / credits (news_watch is
  // paid in credits on every tier since pricing v2; conversation_personas /
  // brand_conversation_runs — 0 hides Conversations),
  // not visibility. competitorsEnabled is forced ON too since pricing v2
  // (2026-11-04): every tier pays for a `competitors` gauge (3/10/25/25), so a
  // per-deal flag would hide what they bought. reportsEnabled +
  // productModuleEnabled stay driven by stored flags / per-deal opt-in.
  return {
    ...(flags ?? {}),
    tasksEnabled: true,
    newsWatchEnabled: true,
    competitorsEnabled: true,
    conversationsEnabled: true,
  };
}

/**
 * White-label branding applied to exported reports (PDF + PPTX) and to the
 * Reports section cover pages. Per-tenant, not per-brand — an agency keeps one
 * identity across all the client brands it manages.
 */
export interface AgencyBranding {
  agencyName: string;
  logoUrl?: string;
  primaryColor?: string;
  footerText?: string;
}

/**
 * Agency credit subscription config (the negotiated commercial terms). The live
 * BALANCE is NOT here — it lives on the usage-counters CREDIT#balance row
 * (CreditBalance in credits.ts). This is just what the next invoice.paid should
 * grant. Populated only for tier='agency'.
 */
export interface AgencyCreditConfig {
  monthlyAllocation: number; // credits granted each billing period
  pricePerCreditCents?: number; // negotiated rate, for reporting
  /**
   * Per-period high-water mark of PRORATED credits already granted this cycle
   * (initial replenish + every mid-cycle upgrade). An upgrade grants only the
   * shortfall up to the new plan's prorated target, so repeated up/down toggles
   * within one period never double-grant. Reset when `proratedPeriodEnd` rolls.
   */
  proratedGrantedThisPeriod?: number;
  /** ISO period-end the high-water mark is scoped to; a newer end resets it. */
  proratedPeriodEnd?: string;
  /**
   * A scheduled DOWNGRADE that takes effect at the period end (downgrades don't
   * apply instantly — the current plan + credits stay live until then). Cleared
   * when the downgrade lands (renewal) or the user re-upgrades before it. The UI
   * shows "Passage à {plan} le {date}".
   */
  pendingMonthlyAllocation?: number;
  pendingEffectiveAt?: string; // ISO period-end
}

export interface TenantSettings {
  quotaOverrides?: QuotaOverrides;
  featureFlags?: FeatureFlags;
  modelOverrides?: Record<string, string>;
  /**
   * Platform-admin-only lift of Cibles TECHNICAL limits (docs/features/targets.md §20.5) — the
   * same family as `modelOverrides`: internal, never sold, no tenant-facing UI. NOT a
   * `quotaOverrides` entry (those are commercial allowances). Clamped server-side to
   * `TARGET_LIMITS.maxContenusPerBrandCeiling`.
   */
  targetLimitOverrides?: { maxContenusPerBrand?: number };
  /**
   * Platform-admin-only « load test » mode (00b DQ14): the tenant's Brand Perception
   * runs on the NEW prompt pipeline get canned engine answers (no provider call).
   * Deliberately NOT a feature flag: `effectiveFeatureFlags` forces every flag on for
   * agency. Accepted by the admin API only where `ENGINE_STUB_ALLOWED=1` (dev), and
   * honoured by the worker only where `BP_ENGINE_STUB_ALLOWED=1` (dev): never on prod.
   */
  engineStub?: boolean;
  webhookUrl?: string;
  customDomain?: string;
  agencyBranding?: AgencyBranding;
  /** Agency-only: credit subscription terms (allocation/rate). NOT the balance. */
  agencyCredits?: AgencyCreditConfig;
  /**
   * Enterprise-only: tenant-wide BP measurement engine selection, set by platform
   * admin at tenant creation (/admin/tenants/new) and run automatically every
   * audit. Internal engine ids (perplexity/openai/gemini/claude + secondary like
   * mistral). Resolved tenant-wide for enterprise by the Trigger Lambda
   * (resolveAllowedEngines) — agency uses the per-brand brand.auditConfig.enginesEnabled
   * instead. Empty/unset ⇒ the tier's primary roster (secondary never auto-runs).
   * See docs/runbooks/adding-bp-engines.md §4.
   */
  enabledEngines?: string[];
  /**
   * Per-tenant report template: which sections appear in generated reports.
   * One config for the whole agency (consistent deliverable across client
   * brands), mirroring agencyBranding. A missing section defaults to ENABLED.
   * Shape lives in report.ts (ReportTemplate) — kept as a loose record here to
   * avoid a types cycle (tenant.ts ← report.ts already imports audit types).
   */
  reportTemplate?: {
    audit?: Record<string, boolean>;
    status?: Record<string, boolean>;
  };
  /**
   * Quota-extension packs currently active, as `{ metric: extraAmount }`.
   * Recomputed from the Stripe subscription's pack items (packsFromSubscription)
   * on every subscription.updated / invoice.paid, then read by the metering gate
   * (which can't call Stripe on the hot path). Stacks on tier defaults; clamped
   * by systemCeiling. See docs/billing/metering-and-entitlements.md §4.3.
   */
  activePacks?: Record<string, number>;
  /**
   * Per-tenant WAF-verify token. Traaker sends it as the `X-Traaker-Verify` header on
   * outbound fetches to the retailer's site (sitemap discovery + catalogue optimize),
   * so the retailer's IT can allow our crawler via a WAF header-match rule that is far
   * more defensible than the public/spoofable User-Agent. Deliberately LOW-sensitivity
   * and displayed in Settings (its whole purpose is to be handed to IT) — unlike the
   * WordPress/webhook secrets which live in dedicated hidden DDB items. Generated +
   * rotated on demand via POST /tenants/waf-verify-token; absent until first generated.
   */
  wafVerifyToken?: string;
  /**
   * Trial-lifecycle email sequence state (E1–E5, plan §9). Written ONLY by the
   * daily `reap-trials` cron's `sendLifecycleEmails` pass + the audit-finalize
   * E1 hook. Purely a notification record: nothing here affects access, limits
   * or billing (see docs/billing/metering-and-entitlements.md §9.5).
   */
  trialLifecycle?: TrialLifecycleState;
  /**
   * RGPD erasure opt-in, set by the customer in Réglages → Facturation BEFORE or
   * AFTER cancelling (Stripe's Customer Portal owns the cancel screen, so the
   * choice cannot live there). It only changes WHEN the account is purged, never
   * whether: `customer.subscription.deleted` stamps `purgeDueAt` at
   * end + CANCEL_PURGE_GRACE_DAYS when true, end + CANCEL_RETENTION_DAYS when
   * false/absent. Toggling it while already cancelled recomputes `purgeDueAt`.
   * Undefined = not chosen = the long retention. See docs/billing/signup-and-trials.md.
   */
  deleteDataOnCancel?: boolean;
}

/**
 * Billing lifecycle:
 *   pending   — tenant created, no payment confirmed yet (can log in, read-only)
 *   active    — payment confirmed (Stripe webhook or manual admin flip)
 *   past_due  — a Stripe invoice failed; grace period before suspend
 *   suspended — BillingGuard blocks cost-bearing routes
 *   cancelled — terminal state
 */
export type BillingStatus =
  | 'pending'
  | 'trialing' // self-serve trial (time-bound) or agency credit-trial — app is usable
  | 'active'
  | 'past_due'
  | 'suspended'
  | 'cancelled';

/**
 * stripe   — Stripe Subscription drives billingStatus via webhooks
 * manual   — payment handled outside Stripe (cash, bank transfer, etc.); admin flips status by hand
 */
export type BillingMode = 'stripe' | 'manual';

/** How this tenant account was originally created. Extensible — 'partner' will be added
 *  when the partner portal ships. Absent on tenants created before this field existed. */
export type TenantProvenance = 'admin' | 'self' | 'partner';

/**
 * Ad-attribution captured from the signup URL, stamped ONCE at tenant creation
 * and never updated — it records how this account was acquired, so a later
 * session must not overwrite it.
 *
 * Set only when the marketing site forwards the params to
 * `app.traaker.ai/signup?...`; `Signup.tsx` stashes them in localStorage to
 * survive the Cognito redirect (same rail as tier/brand/lang), and
 * `Onboarding.tsx` posts them with `POST /tenants/onboard`.
 *
 * `gclid` is the Google Ads click id. It — not the `_gcl_aw` cookie — is the
 * value Google's offline-conversion import keys on, and it's the only one
 * readable here anyway: the cookie is set host-only on `traaker.ai`, so
 * `app.traaker.ai` cannot see it. Its PRESENCE is also what drives HubSpot's
 * `acquisition_channel` (`inbound_ads` vs `sales_rep`).
 *
 * GDPR: this is advertising data tied to an identifiable account. It's stored
 * unconditionally today because the marketing site doesn't yet pass a consent
 * signal across the redirect — see docs/website/marketing-site.md (open drift:
 * the privacy policy still claims no third-party tracking).
 */
export interface TenantAttribution {
  gclid?: string;
  utmSource?: string;
  utmCampaign?: string;
  /** ISO timestamp of capture (= tenant creation). */
  capturedAt: string;
}

export interface Tenant {
  tenantId: string;
  name: string;
  tier: Tier;
  ownerId: string;
  /** Owner email captured at tenant-create time. Stable even after `ownerId`
   * is relinked to the Cognito sub on first login. Absent on tenants created
   * before this field existed (renders `—` in admin). */
  ownerEmail?: string;
  domain: string;
  settings: TenantSettings;
  billingStatus: BillingStatus;
  billingMode: BillingMode;
  /** Account provenance — set once at creation, never updated. Absent on pre-existing tenants. */
  createdBy?: TenantProvenance;
  /** Stripe subscription price in cents per billing period. Written/updated from Stripe webhook events.
   *  Absent on pre-subscription tenants (trialing, pending). Cleared on cancellation. */
  subscriptionAmountCents?: number;
  /** Billing period for subscriptionAmountCents. Use to compute monthly MRR (yearly ÷ 12). */
  subscriptionInterval?: 'month' | 'year';
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  /**
   * HubSpot Company object id, cached by the hubspot-sync Lambda on its first
   * successful sync (resolved by domain search, or minted by a create). Purely a
   * cache: losing it costs one extra search-by-domain, never a duplicate Company.
   *
   * And it CAN be lost, deliberately: a PATCH that 404s means the Company was
   * deleted or merged in HubSpot, so the Lambda REMOVEs this attribute and
   * re-resolves by domain in the same run. Without that path a stale id would be
   * permanent (404 is non-retryable) and the tenant's sync silently dead.
   * Mirrors how `stripeCustomerId` is stored. See docs/platform/hubspot-sync.md.
   */
  hubspotCompanyId?: string;
  /**
   * HubSpot Contact object id for the tenant's ADMIN (owner) user, cached by the
   * same Lambda on the same terms as `hubspotCompanyId` above — resolved by
   * searching on the owner's email, else created and associated to the Company.
   *
   * Caching matters MORE here than for the Company, and not for cost: HubSpot's
   * search index is eventually consistent, so a search run seconds after a create
   * can legitimately miss the Contact it just made and create a duplicate person.
   * The cache plus the 409-parse in `resolveContactId` is what closes that.
   *
   * Still only a cache — losing it costs one search. See docs/platform/hubspot-sync.md §8.
   */
  hubspotContactId?: string;
  /** Ad attribution captured at signup — write-once, see `TenantAttribution`. */
  attribution?: TenantAttribution;
  contractAmountCents?: number;
  contractInterval?: 'month' | 'year';
  contractNotes?: string;
  /**
   * Scheduled tier change (typically a downgrade) that applies at the next
   * billing-period boundary. Promoted by the `customer.subscription.updated`
   * webhook. See docs/billing/pricing-and-billing.md §9.5.
   */
  pendingTier?: Tier;
  pendingTierEffectiveAt?: string; // ISO
  /**
   * Subscription cancellation signal. When the user schedules a cancel in the
   * Stripe Customer Portal, Stripe keeps the sub `active` until the period end
   * and fires `customer.subscription.updated` with `cancel_at_period_end=true`.
   * The webhook captures it here so the UI can show "Résiliation au {date}" and
   * the access stays live until `subscriptionEndsAt`. Cleared on reactivation
   * (cancel undone) and on the terminal `subscription.deleted` (→ `cancelled`).
   */
  cancelAtPeriodEnd?: boolean;
  subscriptionEndsAt?: string; // ISO — current_period_end while a cancel is pending
  /**
   * Trial state. A trial is a tenant state, NOT a tier — limits resolve to the
   * trialed `tier` exactly as normal. See docs/billing/metering-and-entitlements.md §9.
   * `trialMode` is copied from DEFAULT_TRIAL_MODE at SIGNUP and never re-derived
   * from the global, so flipping the global default never breaks in-flight trials.
   */
  trialEndsAt?: string; // ISO; absent = not trialing (agency credit-trial has none)
  trialMode?: 'require_card' | 'block_on_expiry';
  /**
   * Sparse `trial-index` GSI partition key. Set to the constant `TRIAL_INDEX_PK`
   * ONLY while a block_on_expiry trial is live (so the daily trial-lifecycle job
   * can range-query expired trials by `trialEndsAt` instead of scanning). Cleared
   * (REMOVE) the moment the tenant converts to active, dropping it out of the index.
   */
  trialIndexPK?: string;
  /**
   * Post-cancellation purge date (ISO). Stamped by the terminal
   * `customer.subscription.deleted` webhook: subscription end +
   * CANCEL_PURGE_GRACE_DAYS when `settings.deleteDataOnCancel` is true,
   * + CANCEL_RETENTION_DAYS otherwise. The daily `reap-trials` cron's
   * `reapCancelledTenants` pass runs the full deleteTenant cascade once it's due.
   * Cleared (REMOVE, with `purgeIndexPK`) on reactivation — a resubscribed tenant
   * must never be purged.
   */
  purgeDueAt?: string;
  /**
   * Sparse `purge-index` GSI partition key. Set to the constant `PURGE_INDEX_PK`
   * alongside `purgeDueAt` so the reaper range-queries due tenants by date instead
   * of scanning. Only cancelled tenants carry it.
   */
  purgeIndexPK?: string;
  /**
   * Monthly credit grant schedule of a NON-agency subscription (pricing v2,
   * 2026-11-04 — CreditGrantService). Agency keeps its `invoice.paid` replenish.
   * `creditGrantIndexPK` = the constant `CREDIT_GRANT_INDEX_PK`, set ONLY while a
   * subscription whose Price carries `metadata.metric = 'credit'` is live (sparse
   * `credit-grant-index` GSI, SK `nextCreditGrantAt`); REMOVEd by
   * `customer.subscription.deleted`. Grant k lands on
   * `creditGrantDate(creditGrantAnchorAt, k)`, monthly AND yearly alike.
   */
  creditGrantIndexPK?: string;
  /** ISO date of the next monthly credit grant (GSI sort key). */
  nextCreditGrantAt?: string;
  /** ISO anchor of the grant schedule: the subscription's `billing_cycle_anchor`. */
  creditGrantAnchorAt?: string;
  /** The Stripe subscription the schedule belongs to (part of every grant's idempotency key). */
  creditGrantSubId?: string;
  /** Card mode snapshot at signup (from SIGNUP_CARD_MODE). Drives both checkout-forcing
   *  and trial expiry, per-tenant so flipping the global never breaks in-flight trials. */
  signupCardMode?: 'no_card' | 'card_required';
  /** ToS acceptance captured at Stripe Checkout (consent_collection). */
  tosAcceptedAt?: string;
  tosVersion?: string;
  createdAt: string;
  updatedAt: string;
}
